import { useCallback, useEffect, useState } from "react";
import { BlockConfigurationRecord } from "models/view-models/block-configurations/block-configuration-record";
import { ChecklistItemRecord } from "models/view-models/block-configurations/checklist-item-record";
import ChecklistBlockConfigurationChecklistItemService from "utilities/services/block-configurations/checklistitems/checklist-block-configuration-checklist-items-service";
import { CollectionUtils } from "utilities/collection-utils";
import PublicationService, {
    PublicationListQueryParams,
} from "utilities/services/publications/publication-service";
import PublicPublicationService from "utilities/services/public/publications/publication-service";
import PublicationRecord from "models/view-models/publication-record";
import { useSortChecklistItems } from "utilities/hooks/domain/block-configurations/use-sort-checklist-items";
import { useSharedPageContext } from "utilities/contexts/shared-page/shared-page-context";
import SharedChecklistBlockConfigurationChecklistItemService from "utilities/services/shared-checklist-items/shared-checklist-block-configuration-checklist-items-service";

interface UseChecklistBlockChecklistItems {
    errorMessages: string[];
    items: ChecklistItemRecord[];
    loadItems: () => Promise<void>;
    onItemsReordered: (
        updatedItems: ChecklistItemRecord[]
    ) => Promise<number[]>;
    updateItems: (items: ChecklistItemRecord[]) => void;
}

const DEFAULT_ERROR_MESSAGE: string =
    "Could not fetch publications for referenced sections";
const ERROR_MESSAGE_DELIMITER: string = ";";
export const useChecklistBlockChecklistItems = (
    blockConfiguration: BlockConfigurationRecord
): UseChecklistBlockChecklistItems => {
    const { list: listItemsApi } =
        ChecklistBlockConfigurationChecklistItemService.useNestedList();
    const { pageGuid, recipientGuid } = useSharedPageContext();
    const publicationService = pageGuid
        ? PublicPublicationService
        : PublicationService;
    const { list: listPublicationsApi } = publicationService.useList();
    const [items, setItems] = useState<ChecklistItemRecord[]>([]);
    const [errorMessages, setErrorMessages] = useState<string[]>([]);
    const { sortedItems, onItemsReordered } = useSortChecklistItems(items);
    const { list: listSharedItemsApi } =
        SharedChecklistBlockConfigurationChecklistItemService.useNestedList();
    SharedChecklistBlockConfigurationChecklistItemService.useNestedList();

    const attachPublicationsToItems = useCallback(
        (
            items: ChecklistItemRecord[],
            publications: PublicationRecord[]
        ): ChecklistItemRecord[] =>
            items.map((ci) => {
                if (
                    CollectionUtils.isEmpty(ci.note?.checklistItemNoteSections)
                ) {
                    return ci;
                }

                const checklistItemNoteSections =
                    ci.note.checklistItemNoteSections;
                const checklistItemNoteSectionsWithPublications =
                    checklistItemNoteSections.map((cins) => {
                        if (cins.section?.publicationId == null) {
                            return cins;
                        }

                        const publication = publications.find(
                            (p) => p.id === cins.section!.publicationId
                        );

                        if (publication == null) {
                            return cins;
                        }

                        const updatedSection = cins.section.with({
                            publication: publication,
                        });

                        return cins.with({ section: updatedSection });
                    });

                const updatedNote = ci.note.with({
                    checklistItemNoteSections:
                        checklistItemNoteSectionsWithPublications,
                });

                return ci.with({ note: updatedNote });
            }),
        []
    );

    const getDistinctPublicationIds = useCallback(
        (items: ChecklistItemRecord[]): number[] => {
            const checklistItemNoteSections = items.flatMap(
                (ci) => ci?.note?.checklistItemNoteSections
            );
            const publicationIds = checklistItemNoteSections
                .filter((cins) => cins?.section?.publicationId != null)
                .map((cins) => cins!.section!.publicationId!);

            return Array.from(new Set(publicationIds));
        },
        []
    );

    const loadPublications = useCallback(
        async (
            publicationIds: number[]
        ): Promise<PublicationRecord[] | null> => {
            if (CollectionUtils.isEmpty(publicationIds)) {
                return [];
            }

            try {
                const queryParams: PublicationListQueryParams = {
                    publicationIds: publicationIds,
                };

                const response = await listPublicationsApi(queryParams);

                if (
                    response?.resultObjects == null ||
                    response.results == null
                ) {
                    throw new Error(DEFAULT_ERROR_MESSAGE);
                }

                if (response.results.hasErrors()) {
                    throw new Error(
                        response.results
                            .listErrorMessages()
                            .join(ERROR_MESSAGE_DELIMITER)
                    );
                }

                return response.resultObjects;
            } catch (error) {
                const errorMessage =
                    error instanceof Error
                        ? error.message
                        : DEFAULT_ERROR_MESSAGE;

                setErrorMessages(errorMessage.split(ERROR_MESSAGE_DELIMITER));

                return null;
            }
        },
        [listPublicationsApi]
    );

    const loadItems = useCallback(async (): Promise<
        ChecklistItemRecord[] | null
    > => {
        try {
            if (blockConfiguration?.id == null || blockConfiguration.id <= 0) {
                throw new Error("Invalid Block Configuration ID.");
            }

            const response = pageGuid
                ? await listSharedItemsApi(
                      {
                          pageGuid: pageGuid,
                          configurationId: blockConfiguration.id,
                      },
                      { recipient: recipientGuid }
                  )
                : await listItemsApi({
                      configurationId: blockConfiguration.id,
                  });

            if (response?.resultObjects == null || response.results == null) {
                throw new Error("No response received");
            }

            if (response.results.hasErrors()) {
                throw new Error(response.results.listErrorMessages().join(";"));
            }

            return response.resultObjects;
        } catch (error) {
            const errorMessage =
                error instanceof Error ? error.message : DEFAULT_ERROR_MESSAGE;

            setErrorMessages(errorMessage.split(ERROR_MESSAGE_DELIMITER));

            return null;
        }
    }, [
        blockConfiguration.id,
        listItemsApi,
        listSharedItemsApi,
        pageGuid,
        recipientGuid,
    ]);

    const loadItemsWithPublications = useCallback(async (): Promise<void> => {
        const items = await loadItems();

        if (CollectionUtils.isEmpty(items)) {
            setItems([]);
            return;
        }

        const publicationIds = getDistinctPublicationIds(items);
        const publications = await loadPublications(publicationIds);

        if (CollectionUtils.isEmpty(publications)) {
            setItems(items);
            return;
        }

        const itemsWithPublications = attachPublicationsToItems(
            items,
            publications
        );

        setItems(itemsWithPublications);
    }, [
        attachPublicationsToItems,
        getDistinctPublicationIds,
        loadItems,
        loadPublications,
    ]);

    const updateItems = useCallback((newItems: ChecklistItemRecord[]): void => {
        setItems(newItems);
    }, []);

    useEffect(() => {
        loadItemsWithPublications();
    }, [loadItemsWithPublications]);

    return {
        errorMessages: errorMessages,
        items: sortedItems,
        loadItems: loadItemsWithPublications,
        onItemsReordered: onItemsReordered,
        updateItems: updateItems,
    };
};
